import React, { FC, useMemo } from "react";
import {
  ContextualMenu,
  Dialog,
  DialogType,
  IButtonProps,
  IDragOptions,
  IModalProps,
  Text,
} from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { logger, useTheme } from "cayo.ui";
import styled from "styled-components";
import { ConfirmationBody } from "@cayo/shared/components/dialogs/ConfirmationModal";
import Form from "../Form";
import { CollectActionParametersDialogProps } from "./types";
import { useDialogSize } from "./useDialogSize";
import { useDynamicTitle } from "./useDynamicTitle";
import { useExternalSubmit } from "./useExternalSubmit";
import useTypeAnnotations from "../GlobalHooks/type-annotations.hook";

const log = logger.getLogger("CollectActionParametersDialog");

const CollectActionParametersDialog: FC<CollectActionParametersDialogProps> = (props) => {
  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");
  const {
    description,
    externalObjects,
    actionItemDisplayDescription,
    helpLink,
    display,
    items,
    lastModifiedDateTime,
    onClose,
  } = props;

  const { allowMaximize, size } = display || {};

  const { cayoTheme } = useTheme();
  const { getIconAnnotation } = useTypeAnnotations();
  const { dialogSize, handleMaximize } = useDialogSize(size);

  const dragOptions: IDragOptions = {
    moveMenuItemText: "Move",
    closeMenuItemText: "Close",
    menu: ContextualMenu,
    keepInBounds: true,
  };

  const modalProps: IModalProps = useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: true,
      dragOptions: dialogSize.isMaximized ? undefined : dragOptions,
    }),
    [labelId, subTextId, dialogSize.isMaximized]
  );

  const maximizeButtonProps = {
    iconProps: { iconName: dialogSize.isMaximized ? "BackToWindow" : "FullScreen" },
    onClick: handleMaximize,
  };

  const title = useDynamicTitle(props, log);
  const onSubmit = useExternalSubmit(props);

  const hasInputControls = items && items.length > 0;

  const ToHelpButton = (link: string) => {
    return {
      iconProps: { iconName: "help" },
      onClick: () => {
        const a = document.createElement("a");
        a.href = link;
        a.target = "_blank";
        a.click();
      },
    } as IButtonProps;
  };

  const topButtonsProps: IButtonProps[] = [
    lastModifiedDateTime && {
      // datetime rendering as a disabled icon button
      onRenderIcon: () => (
        <Text
          styles={{
            root: {
              color: cayoTheme.brandColors.disabled,
              padding: "0 8px",
            },
          }}
        >
          {lastModifiedDateTime}
        </Text>
      ),
      disabled: true,
      styles: {
        root: {
          width: "auto",
        },
        rootDisabled: {
          backgroundColor: "transparent",
        },
      },
    },
    helpLink && ToHelpButton(helpLink),
    allowMaximize && maximizeButtonProps,
  ].filter((item): item is IButtonProps => Boolean(item));

  return (
    <Container>
      <Dialog
        hidden={false}
        onDismiss={() => onClose?.()}
        dialogContentProps={{
          type: DialogType.close,
          title,
          topButtonsProps,

          styles: {
            subText: {
              marginTop: hasInputControls ? 8 : 24,
              textAlign: hasInputControls ? "left" : "center",
            },
          },
        }}
        styles={{
          main: {
            minWidth: `${dialogSize.width}px !important`,
            minHeight: `${dialogSize.height}px !important`,
            position: "relative",
          },
        }}
        modalProps={modalProps}
      >
        {description && (
          <ConfirmationBody
            confirmationMessage={description}
            objects={externalObjects}
            actionItemDisplayDescription={actionItemDisplayDescription}
          />
        )}

        <Form
          {...props}
          getIconAnnotation={getIconAnnotation}
          onSubmit={externalObjects?.length ? onSubmit : props.onSubmit}
          title=""
          className="static-form"
        />
      </Dialog>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: stretch;
  height: 100%;
  flex-flow: column;
  position: absolute;
  inset: 0;
`;

export default CollectActionParametersDialog;
